import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiPhoneOff, FiMic, FiMicOff, FiVideo, FiVideoOff, FiMessageCircle } from 'react-icons/fi';
import { HiOutlineSpeakerWave, HiOutlineSpeakerXMark } from 'react-icons/hi2';
import { off, onValue, ref } from 'firebase/database';
import { database } from '../constants/firebase';
import { DailyVideo } from '@daily-co/daily-react';


const OutgoingCallPopup = ({
    user,
    onMinimize,
    onCancel,
    onToggleMute,
    onToggleSpeaker,
    onToggleVideo,
    onOpenChat,
    initialMuted = false,
    initialSpeaker = false,
    initialVideo = false,
    onClose,
    callStatus = 'calling', // "calling", "connecting", "connected"
    callDuration = 0, // in seconds
    participantIds,
    localSessionId,
    callType = "audio", // 👈 added fallback
    handledByApp,
}) => {
    const [participantProfiles, setParticipantProfiles] = useState({});

    const name = user?.name
        ? user.name
        : user?.groupId
            ? user?.name || "Unknown Group"
            : "Unknown User";

    const avatar =
        user?.groupId ?
            user?.photo || 'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346'
            :
            user?.profilePic ||
            'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef';

    const [muted, setMuted] = useState(initialMuted);
    const [speaker, setSpeaker] = useState(initialSpeaker);
    const [timer, setTimer] = useState(callDuration);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (!user?.groupId) return;

        const participantsRef = ref(database, `groupCalls/${user.groupId}/participants`);

        const unsubParticipants = onValue(participantsRef, (snap) => {
            if (!snap.exists()) {
                setParticipantProfiles({});
                return;
            }

            const participants = snap.val();

            console.log("Participants: ", participants);

            Object.entries(participants).forEach(([uid, data]) => {
                const userRef = ref(database, `Users/${uid}`);
                console.log("Particpat: ", data);


                onValue(userRef, (userSnap) => {
                    if (userSnap.exists()) {
                        const profile = userSnap.val();

                        setParticipantProfiles((prev) => ({
                            ...prev,
                            [data.sessionId]: {
                                ...profile,
                                uid,
                            },
                        }));
                    }
                });
            });
        });

        return () => unsubParticipants();
    }, [user?.groupId]);



    useEffect(() => {
        let interval;
        if (callStatus === 'connected') {
            interval = setInterval(() => setTimer((prev) => prev + 1), 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [callStatus]);

    const toggleMute = () => {
        const next = !muted;
        setMuted(next);
        onToggleMute?.(next);
    };

    const toggleSpeaker = () => {
        const next = !speaker;
        setSpeaker(next);
        onToggleSpeaker?.(next);
    };

    const toggleVideo = () => {
        const next = !initialVideo;
        onToggleVideo?.(next);
    };

    const getStatusText = () => {
        switch (callStatus) {
            case 'ringing':
                return 'Ringing...';
            case 'connected':
                return formatTime(timer);
            default:
                return 'Calling...';
        }
    };

    return (
        <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] flex flex-col">
                {/* Blurred dim background */}
                <div className="absolute inset-0 bg-center bg-cover" style={{ backgroundImage: user?.groupId ? 'none' : `url(${avatar})` }} />
                <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />

                {/* Back / close */}
                {callStatus === "connected" && !initialVideo && (
                    <button
                        onClick={onMinimize}
                        className="absolute top-4 left-4 p-2 rounded-full hover:bg-white/10 focus:outline-none text-white z-50"
                        aria-label="Minimize call"
                    >
                        <FiArrowLeft className="w-6 h-6" />
                    </button>
                )}

                {/* Center content */}
                <div className="flex-1 flex items-center justify-center px-4 max-h-[65vh] md:max-h-[70vh]">
                    <div className="w-full z-10 max-w-3xl h-[65vh] md:h-[70vh] rounded-sm flex flex-col items-center justify-center select-none">
                        {!(callStatus === "connected" && initialVideo) && (
                            <>
                                <img src={avatar} alt={name} className="w-28 h-28 md:w-36 md:h-36 rounded-full object-cover mb-6 shadow-lg shadow-black/40" />
                                <h2 className="text-white text-xl md:text-2xl font-semibold mb-2 line-clamp-1 max-w-[80%] text-center">{name}</h2>
                                <p className="text-white/70 tracking-widest text-sm uppercase">{getStatusText()}</p>
                            </>
                        )}
                    </div>
                </div>

                {/* Bottom actions row */}
                <div className="w-full px-6 pb-8 z-50">
                    <div className="flex items-center justify-center mb-10 space-x-10">
                        {/* Mute */}
                        <button onClick={toggleMute} className="flex flex-col items-center text-white/80 hover:text-white" disabled={callStatus === 'calling'}>
                            <div
                                className={`
                  w-14 h-14 rounded-full flex items-center justify-center 
                  backdrop-blur-sm transition
                  ${muted ? 'bg-red-500/80 ring-2 ring-red-500/60' : 'bg-white/10 hover:bg-white/15'}
                  ${callStatus === 'calling' ? 'opacity-50' : ''}
                `}
                            >
                                {muted ? <FiMicOff className="w-6 h-6" /> : <FiMic className="w-6 h-6" />}
                            </div>
                            <span className="mt-2 text-xs tracking-wide">{muted ? 'Unmute' : 'Mute'}</span>
                        </button>

                        {/* Speaker */}
                        {/* {callStatus === 'connected' && (
                            <button onClick={toggleSpeaker} className="flex flex-col items-center text-white/80 hover:text-white">
                                <div
                                    className={`
                    w-14 h-14 rounded-full flex items-center justify-center 
                    backdrop-blur-sm transition
                    ${speaker ? 'bg-emerald-400/20 ring-2 ring-emerald-400/60' : 'bg-white/10 hover:bg-white/15'}
                  `}
                                >
                                    {speaker ? <HiOutlineSpeakerWave className="w-6 h-6" /> : <HiOutlineSpeakerXMark className="w-6 h-6" />}
                                </div>
                                <span className="mt-2 text-xs tracking-wide">Speaker</span>
                            </button>
                        )} */}

                        {/* Video toggle */}
                        {callStatus === 'connected' && !user?.groupId && !handledByApp && (
                            <button onClick={toggleVideo} className="flex flex-col items-center text-white/80 hover:text-white">
                                <div
                                    className={`
                    w-14 h-14 rounded-full flex items-center justify-center 
                    backdrop-blur-sm transition
                    ${initialVideo ? 'bg-blue-400/20 ring-2 ring-blue-400/60' : 'bg-white/10 hover:bg-white/15'}
                  `}
                                >
                                    {initialVideo ? <FiVideo className="w-6 h-6" /> : <FiVideoOff className="w-6 h-6" />}
                                </div>
                                <span className="mt-2 text-xs tracking-wide">Video</span>
                            </button>
                        )}

                    </div>

                    {/* Cancel/End call */}
                    <div className="w-full flex items-center justify-center">
                        <button
                            onClick={onCancel}
                            className={`
                md:w-20 md:h-20 w-16 h-16 rounded-full 
                active:scale-95 transition flex items-center justify-center 
                shadow-xl shadow-black/40
                bg-[#E35E4F] hover:bg-[#d95142]
              `}
                            aria-label={callStatus === 'connected' ? 'End call' : 'Cancel call'}
                        >
                            <FiPhoneOff className="w-9 h-9 text-white" />
                        </button>
                    </div>
                </div>

                {/* {callStatus === 'connected' && (
                    <DailyAudio
                        maxSpeakers={6}
                        onPlayFailed={(e) => {
                            console.error(`Failed to play ${e.type} for ${e.sessionId}`, e.target);
                        }}
                    />
                )} */}

                {/* Video background (only when connected + video on) */}
                {callStatus === "connected" && initialVideo && (
                    <div className="absolute inset-0 w-full h-full">
                        {/* ✅ If it's a group call */}
                        {user?.groupId ? (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full h-full p-2">
                                    {participantIds
                                        .filter((id) => id !== localSessionId)
                                        .map((id) => {
                                            const participant = participantProfiles[id];
                                            const displayName = participant?.name ? participant.name : '';

                                            return (
                                                <div
                                                    key={id}
                                                    className="relative w-full h-40 md:h-56 rounded-lg overflow-hidden shadow-lg shadow-black/50 border border-white/10"
                                                >
                                                    <DailyVideo
                                                        sessionId={id}
                                                        type="video"
                                                        fit="cover"
                                                        className="w-full h-full object-cover"
                                                    />
                                                    {
                                                        displayName && (
                                                            <div className="absolute bottom-0 left-0 w-full bg-black/50 text-white text-xs md:text-sm px-2 py-1 truncate">
                                                                {displayName}
                                                            </div>
                                                        )
                                                    }

                                                </div>
                                            );
                                        })}
                                </div>

                                {/* Local self-view (bottom-right) */}
                                {localSessionId && (
                                    <div className="absolute bottom-4 right-4 w-32 h-48 rounded-lg overflow-hidden shadow-lg shadow-black/50 border border-white/20">
                                        <DailyVideo
                                            sessionId={localSessionId}
                                            automirror
                                            type="video"
                                            fit="cover"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            /* ✅ If it's 1-to-1 call */
                            <>
                                {/* Remote participant fills the screen */}
                                {participantIds
                                    .filter((id) => id !== localSessionId)
                                    .map((id) => (
                                        <DailyVideo
                                            key={id}
                                            sessionId={id}
                                            type="video"
                                            fit="cover"
                                            className="w-full h-full object-cover"
                                        />
                                    ))}

                                {/* Local self-view (bottom-right) */}
                                {localSessionId && (
                                    <div className="absolute bottom-4 right-4 w-32 h-48 rounded-lg overflow-hidden shadow-lg shadow-black/50 border border-white/20">
                                        <DailyVideo
                                            sessionId={localSessionId}
                                            automirror
                                            type="video"
                                            fit="cover"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}


            </motion.div>
        </AnimatePresence>
    );
};

export default OutgoingCallPopup;
